import type { FC, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import { Stack, useTheme } from '@mui/material';

import { styles } from './index.styles';

// 0.8 = 80% to create the gap at the bottom
const SPEEDOMETER_RADIUS = 46;
const SPEEDOMETER_CIRCUMFERENCE = 0.8 * 2 * SPEEDOMETER_RADIUS * Math.PI;

// inner ring showing cross-survey benchmark
const SPEEDOMETER_BENCHMARK_RADIUS = 41;
const SPEEDOMETER_BENCHMARK_CIRCUMFERENCE =
  0.8 * 2 * SPEEDOMETER_BENCHMARK_RADIUS * Math.PI;
const SPEEDOMETER_MAX = 999;

interface SpeedometerChartProps {
  children: ReactNode;
  percentage: number;
  height?: number | string;
  color?: string;
  benchmarkPercentage?: number | null;
  benchmarkColor?: string;
}

const SpeedometerChart: FC<SpeedometerChartProps> = ({
  children,
  percentage,
  height = '100%',
  color,
  benchmarkPercentage,
  benchmarkColor,
}) => {
  const theme = useTheme();
  const strokeColor = color ? color : theme.colors.secondary[500];
  const svgRef = useRef<SVGSVGElement>(null);

  const strokeDashArray = useMemo(
    () => `${percentage * SPEEDOMETER_CIRCUMFERENCE}, ${SPEEDOMETER_MAX}`,
    [percentage],
  );

  const showBenchmark = typeof benchmarkPercentage === 'number';

  const benchmarkStrokeDashArray = useMemo(
    () =>
      `${(benchmarkPercentage ?? 0) * SPEEDOMETER_BENCHMARK_CIRCUMFERENCE}, ${SPEEDOMETER_MAX}`,
    [benchmarkPercentage],
  );

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'center',
        height: height,
        position: 'relative',
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width="auto"
        height="100%"
        fill="none"
        strokeLinecap="round"
      >
        <g
          // rotate so the gap of the circles are at the bottom
          transform="rotate(126, 50, 50)"
        >
          {/* background */}
          <svg
            stroke={theme.colors.neutral[100]}
            strokeWidth={7.5}
            // 80% of the circumference
            strokeDasharray={`${SPEEDOMETER_CIRCUMFERENCE}, 999`}
          >
            <circle cx="50" cy="50" r={SPEEDOMETER_RADIUS} />
          </svg>
          {/* foreground */}
          <svg
            ref={svgRef}
            strokeWidth={7.5}
            stroke={strokeColor}
            strokeDasharray={strokeDashArray}
            style={styles.foreground}
          >
            <circle cx="50" cy="50" r={SPEEDOMETER_RADIUS} />
          </svg>
          {showBenchmark && (
            <svg
              strokeWidth={5}
              stroke={benchmarkColor ? benchmarkColor : theme.colors.black}
              strokeDasharray={benchmarkStrokeDashArray}
              data-test-id="speedometer-benchmark-ring"
            >
              <circle cx="50" cy="50" r={SPEEDOMETER_BENCHMARK_RADIUS} />
            </svg>
          )}
        </g>
      </svg>
      <Stack direction="column" sx={styles.centerElement}>
        {children}
      </Stack>
    </Stack>
  );
};

export default SpeedometerChart;
